import { UserManager } from './user-manager';
import { type User, type UsersData } from '../types';
import { logger } from '../utils/logger';

// Global reference to the current SDK's UserManager instance
let currentUserManager: UserManager | null = null;

/**
 * Set the UserManager instance (called by SuperatomSDK during initialization)
 * This should be called with the SDK's UserManager instance
 * @param userManager - UserManager instance from SuperatomSDK
 */
export function setUserManager(userManager: UserManager): void {
  if (!userManager) {
    throw new Error('userManager cannot be null');
  }
  currentUserManager = userManager;
  logger.debug('UserManager instance set');
}

/**
 * Get the current UserManager instance
 */
export function getUserManager(): UserManager {
  if (!currentUserManager) {
    throw new Error(
      'UserManager not initialized. Make sure SuperatomSDK is initialized before using user storage.'
    );
  }
  return currentUserManager;
}

/**
 * Load users from memory (UserManager maintains the cache)
 * @returns UsersData object containing all users
 */
export function loadUsers(): UsersData {
  const manager = getUserManager();
  return {
    users: manager.getAllUsers()
  };
}

/**
 * Save users to file immediately (forces sync)
 * @param usersData - UsersData object to save
 */
export async function saveUsers(usersData: UsersData): Promise<void> {
  const manager = getUserManager();

  // Clear existing users and repopulate
  manager.deleteAllUsers();

  // Add all users from the provided data
  for (const user of usersData.users) {
    try {
      manager.createUser(user);
    } catch (error) {
      // User might already exist, update instead
      if (manager.userExists(user.username)) {
        manager.updateUser(user.username, user);
      }
    }
  }

  // Force immediate sync to file
  await manager.forceSync();
}

/**
 * Find a user by username
 * @param username - Username to search for
 * @returns User object if found, null otherwise
 */
export function findUserByUsername(username: string): User | null {
  const manager = getUserManager();
  const user = manager.getUser(username);
  return user || null;
}

/**
 * Find a user by email
 * @param email - Email to search for
 * @returns User object if found, null otherwise
 */
export function findUserByEmail(email: string): User | null {
  const manager = getUserManager();
  const user = manager.getUserByEmail(email);
  return user || null;
}

/**
 * Find a user by username or email
 * @param identifier - Username or email to search for
 * @returns User object if found, null otherwise
 */
export function findUserByUsernameOrEmail(identifier: string): User | null {
  const manager = getUserManager();
  const user = manager.getUserByUsernameOrEmail(identifier);
  return user || null;
}

/**
 * Add WebSocket ID to a user's wsIds array
 * @param username - Username to update
 * @param wsId - WebSocket ID to add
 * @returns true if successful, false otherwise
 */
export function addWsIdToUser(username: string, wsId: string): boolean {
  try {
    const manager = getUserManager();
    return manager.addWsId(username, wsId);
  } catch (error) {
    logger.error('Error adding WebSocket ID:', error);
    return false;
  }
}

/**
 * Remove WebSocket ID from a user's wsIds array
 * @param username - Username to update
 * @param wsId - WebSocket ID to remove
 * @returns true if successful, false otherwise
 */
export function removeWsIdFromUser(username: string, wsId: string): boolean {
  try {
    const manager = getUserManager();
    return manager.removeWsId(username, wsId);
  } catch (error) {
    logger.error('Error removing WebSocket ID:', error);
    return false;
  }
}

/**
 * Cleanup and destroy the UserManager
 */
export async function cleanupUserStorage(): Promise<void> {
  if (currentUserManager) {
    await currentUserManager.destroy();
    currentUserManager = null;
    logger.info('UserManager cleaned up');
  }
}

// Export types
export type { User, UsersData } from '../types';
